// User: Actions for getting user session and context of logged-in user.
import { Dispatch } from 'redux';

import { get, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { AppState } from '../reducers';
import { customFailed } from '../util/common';
import { featureToggle } from './feature';
import { getSblConsent } from './sbl';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';

type Action<T> = import('./types').Action<T>;
type FetchUserSession = import('../generated-types').Session;
type FetchUserContext = import('../generated-types').User;

export interface FetchUserSessionStartedAction extends Action<typeof actionTypes.FETCH_USER_SESSION_STARTED> {
    type: typeof actionTypes.FETCH_USER_SESSION_STARTED;
}

export interface FetchUserSessionFulfilledAction extends Action<typeof actionTypes.FETCH_USER_SESSION_FULFILLED> {
    type: typeof actionTypes.FETCH_USER_SESSION_FULFILLED;
    payload: FetchUserSession;
}

export interface KycBlockedToggleAction extends Action<typeof actionTypes.KYC_BLOCKED_TOGGLE> {
    type: typeof actionTypes.KYC_BLOCKED_TOGGLE;
    payload: boolean;
}

export interface FetchUserSessionFailedAction extends Action<typeof actionTypes.FETCH_USER_SESSION_FAILED> {
    type: typeof actionTypes.FETCH_USER_SESSION_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface FetchUserContextStartedAction extends Action<typeof actionTypes.FETCH_USER_CONTEXT_STARTED> {
    type: typeof actionTypes.FETCH_USER_CONTEXT_STARTED;
}

export interface FetchUserContextFulfilledAction extends Action<typeof actionTypes.FETCH_USER_CONTEXT_FULFILLED> {
    type: typeof actionTypes.FETCH_USER_CONTEXT_FULFILLED;
    payload: FetchUserContext;
}

export interface FetchUserContextFailedAction extends Action<typeof actionTypes.FETCH_USER_CONTEXT_FAILED> {
    type: typeof actionTypes.FETCH_USER_CONTEXT_FAILED;
    payload: RequestExceptionWithResponse;
}

export type UserActions =
    | FetchUserSessionStartedAction
    | FetchUserSessionFulfilledAction
    | FetchUserSessionFailedAction
    | FetchUserContextStartedAction
    | FetchUserContextFulfilledAction
    | FetchUserContextFailedAction
    | KycBlockedToggleAction;

export const fetchUserSessionStarted = (): FetchUserSessionStartedAction => ({
    type: actionTypes.FETCH_USER_SESSION_STARTED,
});
export const fetchUserSessionFulfilled = (json: FetchUserSession): FetchUserSessionFulfilledAction => ({
    type: actionTypes.FETCH_USER_SESSION_FULFILLED,
    payload: json,
});
export const kycBlockedToggle = (json: boolean): KycBlockedToggleAction => ({
    type: actionTypes.KYC_BLOCKED_TOGGLE,
    payload: json,
});
export const fetchUserSessionFailed = (error: RequestExceptionWithResponse): FetchUserSessionFailedAction => ({
    type: actionTypes.FETCH_USER_SESSION_FAILED,
    payload: error,
});

export const fetchUserContextStarted = (): FetchUserContextStartedAction => ({
    type: actionTypes.FETCH_USER_CONTEXT_STARTED,
});
export const fetchUserContextFulfilled = (json: FetchUserContext): FetchUserContextFulfilledAction => ({
    type: actionTypes.FETCH_USER_CONTEXT_FULFILLED,
    payload: json,
});
export const fetchUserContextFailed = (error: RequestExceptionWithResponse): FetchUserContextFailedAction => ({
    type: actionTypes.FETCH_USER_CONTEXT_FAILED,
    payload: error,
});

export const fetchUserContext = async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
    const { loader, context } = getState();
    const { hasFetchedUserContext, isFetchingUserContext } = loader;
    if (!hasFetchedUserContext && !isFetchingUserContext) {
        try {
            dispatch(fetchUserContextStarted());
            const response: FetchUserContext = await get(
                `${actionTypes.RESOURCE_PATH}/user`,
                getRequestHeaders(context.config.bankregnr),
            );

            dispatch(fetchUserContextFulfilled(response));
        } catch (error) {
            dispatch(fetchUserContextFailed(error));
        }
    }
};

export const fetchUserSession =
    () =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, context } = getState();
        const { isFetchingUserSession, hasFetchedUserSession } = loader;
        if (!hasFetchedUserSession && !isFetchingUserSession) {
            try {
                dispatch(fetchUserSessionStarted());

                const response: FetchUserSession = await get(
                    `${actionTypes.RESOURCE_PATH}/user/session`,
                    getRequestHeaders(context.config.bankregnr),
                );
                await featureToggle('salgspant-traffic-light', dispatch, context.config.bankregnr);
                await featureToggle('salgspant-activate-loan-from-lc', dispatch, context.config.bankregnr);
                await featureToggle('salgspant-show-completion-insurance', dispatch, context.config.bankregnr);

                if (!loader.hasFetchedSblConsent) {
                    getSblConsent(loader, context, dispatch);
                }

                dispatch(fetchUserSessionFulfilled(response));
                if (response.valid) await fetchUserContext(dispatch, getState);
                else dispatch(fetchUserSessionFailed(customFailed('User session is not valid')));
            } catch (error) {
                dispatch(fetchUserSessionFailed(error));
            }
        }
    };
